import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../util/axiosInstance";
import { useLoading } from "../components/LoadingContext";
import { checkSession } from "../util/checkSession";
import { useSurveyContext } from "../context/SurveyContext"; // <-- Import context
import { API_KEY } from "../config";

interface IPriorSleepWakeTool {
  partitionKey: string;
  rowKey: string;
  riskFactor: string;
  hours: number;
}

const Container = styled.div`
  background-color: #000;
  color: white;
  padding: 20px;
  min-height: 78.5vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Logo = styled.img`
  width: 120px;
  margin-bottom: 10px;
`;

const Title = styled.h2`
  color: #FF372F;
  margin: 10px 0;
  text-align: center;
`;

const Form = styled.form`
  width: 100%;
  max-width: 600px;
  margin-top: 20px;
`;

const FieldSet = styled.div`
  margin-bottom: 25px;
`;

const QuestionText = styled.p`
  margin-bottom: 10px;
`;

const Input = styled.input`
  padding: 12px 10px;
  border-radius: 4px;
  border: none;
  width: 100%;
  margin-bottom: 10px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 12px 14px;
    font-size: 14px;
  }
`;

const Button = styled.button`
  background-color: #FF372F;
  color: white;
  padding: 12px 20px;
  font-size: 16px;
  border: none;
  border-radius: 3px;
  margin-top: 30px;
  width: 100%;
  cursor: pointer;

  &:disabled {
    background-color: #888;
    cursor: not-allowed;
  }
`;


const PriorSleepWakeTool: React.FC = () => {
  const { setLoading } = useLoading();
  const { surveyData, setSurveyData } = useSurveyContext(); // <-- Access context updater
  const [questions, setQuestions] = useState<IPriorSleepWakeTool[]>([]);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const navigate = useNavigate();
  const location = useLocation();
  const optionalEmail = location.state?.optionalEmail || "";

  useEffect(() => {
    setLoading(true);
    axiosInstance
      .get(`/GetMasterData/PriorSleepWakeTool?code=${API_KEY}`)
      .then((response) => {
        setQuestions(response.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [setLoading]);

  const handleChange = (rowKey: string, value: string) => {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      const rounded = parseFloat(num.toFixed(1));
      setAnswers((prev) => ({ ...prev, [rowKey]: rounded }));
    } else {
      setAnswers((prev) => ({ ...prev, [rowKey]: 0 }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    checkSession();

    // ⬇️ Update SurveyContext with user answers
    setSurveyData({
      ...surveyData,
      PriorSleepWakeTool: answers,
    });

    navigate("/SelfReportTool", { state: { optionalEmail } });
  };

  const isFormComplete = questions.every((q) => answers[q.rowKey] !== undefined && answers[q.rowKey] !== 0);
  const photo = questions[0]?.partitionKey?.split(":")[0] || "";

  return (
    <Container>
      <Logo src={`${photo}.png`} alt={`${photo} Logo`} />
      <Title>{questions[0]?.partitionKey}</Title>
      <Form onSubmit={handleSubmit}>
        {questions.map((q) => (
          <FieldSet key={q.rowKey}>
            <QuestionText>{q.rowKey} - {q.riskFactor}</QuestionText>
            <Input
              type="number"
              step="0.1"
              min="0"
              placeholder={`Enter hours `}
              value={answers[q.rowKey] ?? ""}
              onChange={(e) => handleChange(q.rowKey, e.target.value)}
            />
          </FieldSet>
        ))}

        <Button type="submit" disabled={!isFormComplete}>
          Continue
        </Button>
      </Form>
    </Container>
  );
};

export default PriorSleepWakeTool;
